import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { CustomLoginService } from '@privatelib/ngx18-authz-dev';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { menus } from 'app/mock-api/common/navigation/data';
import { CVA_AUTH_MODULES } from 'app/models/auth.modules.enum';
import { CVA_CONSTANTS } from 'app/util/constants';
import { AUTH_MODULE_ROUTE_MAP } from 'app/util/route.constants';
import { catchError, from, map, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SideMenuService {
  readonly menus: FuseNavigationItem[] = menus;
  constructor(
    private readonly navigationService: NavigationService,
    private readonly customLoginService: CustomLoginService
  ) { }

  setMenus(isLicenseExpired = false) {
    return from(this.fetchPermissions()).pipe(
      map((permissions) => {
        // Filter menus based on the permissions
        return this.filterMenusByPermission(
          isLicenseExpired ? this.adminMenusOnExpiredLicense() : menus,
          permissions
        );
      }),
      switchMap((filteredMenus) => {
        // Return the post request observable
        return this.navigationService.post(filteredMenus);
      }),
      catchError((error) => {
        console.error("Error while fetching the permissions ", error);
        throw error;
      })
    );
  }

  async fetchPermissions() {
    const permissions: CVA_AUTH_MODULES[] = [
      CVA_AUTH_MODULES.CVA_PRACTITIONER,
      CVA_AUTH_MODULES.CVA_LICENSE_MANAGEMENT,
      CVA_AUTH_MODULES.CVA_SOAP_NOTES,
      CVA_AUTH_MODULES.CVA_CUSTOM_TEMPLATE
    ];

    // Use map to create the array of promises
    const permissionAccess = await Promise.all(
      permissions.map(permission =>
        this.customLoginService.IsAuthorized(permission, CVA_CONSTANTS.PERMISSION.READ)
      )
    );

    // Combine permissions and access results into an object using reduce
    return permissions.reduce((result, permission, index) => {
      result[permission] = permissionAccess[index];
      return result;
    }, {} as Record<CVA_AUTH_MODULES, boolean>);
  }

  filterMenusByPermission(menus: FuseNavigationItem[], permission: Record<CVA_AUTH_MODULES, boolean>): FuseNavigationItem[] {
    return menus.filter(menu => {
      // Check if the route has a permission map
      const routePermissions = AUTH_MODULE_ROUTE_MAP[menu.link.slice(1)];
      if (!routePermissions) {
        // If no permissions are required for this menu, include it
        return true;
      }

      // Check if at least one of the required permissions is true in the permission object
      return routePermissions.some(permissionKey => permission[permissionKey]);
    });
  }

  adminMenusOnExpiredLicense() {
    const allowedMenus = ['id_settings', 'id_help', 'id_signout'];
    return menus.filter(menu => allowedMenus.includes(menu.id))
  }
}
